import { useState, useMemo } from 'react';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import { MapPin, X, Users } from 'lucide-react';

// World topology from react-simple-maps CDN (low-res countries)
const WORLD_TOPO = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

export default function GeoMap({ points = [] }) {
  const [hover, setHover] = useState(null);
  const [selected, setSelected] = useState(null);

  const totalVisitors = points.reduce((a, b) => a + b.visitors, 0);
  const maxVisitors = points[0]?.visitors || 1;

  // Size dots by sqrt(count) so 100 isn't 100× larger than 1.
  const sizedPoints = useMemo(
    () => points.map((p) => ({
      ...p,
      r: Math.max(4, Math.min(22, 4 + Math.sqrt(p.visitors / maxVisitors) * 18)),
    })),
    [points, maxVisitors],
  );

  return (
    <div className="bg-[var(--surface-2)] rounded-2xl border border-[var(--hairline)] overflow-hidden relative" data-testid="geo-map">
      <div className="px-4 pt-4 pb-2 flex items-center gap-2">
        <MapPin size={12} className="text-[#B91C1C]" />
        <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#4B4B52]">
          World visitor map
        </h3>
        <span className="ml-auto text-[11px] text-[#8A8A93]">
          {points.length} {points.length === 1 ? 'city' : 'cities'} · {totalVisitors.toLocaleString()} visitors
        </span>
      </div>

      <div className="bg-white" style={{ height: 420, position: 'relative' }}>
        {points.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center text-[#8A8A93] text-[13px]">
            No geo-tagged traffic yet in this range.
          </div>
        ) : (
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{ scale: 135 }}
            width={980}
            height={420}
            style={{ width: '100%', height: '100%' }}
          >
            <ZoomableGroup center={[0, 20]} zoom={1} maxZoom={8}>
              <Geographies geography={WORLD_TOPO}>
                {({ geographies }) =>
                  geographies.map((geo) => (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill="#F3F4F6"
                      stroke="#E5E7EB"
                      strokeWidth={0.5}
                      style={{
                        default: { outline: 'none' },
                        hover: { fill: '#FEE2E2', outline: 'none' },
                        pressed: { outline: 'none' },
                      }}
                    />
                  ))
                }
              </Geographies>
              {sizedPoints.map((p) => (
                <Marker key={`${p.city}-${p.country}-${p.lat}-${p.lng}`} coordinates={[p.lng, p.lat]}>
                  <circle
                    r={p.r}
                    fill="#B91C1C"
                    fillOpacity={0.22}
                    stroke="#B91C1C"
                    strokeWidth={1.5}
                    style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                    onMouseEnter={() => setHover(p)}
                    onMouseLeave={() => setHover(null)}
                    onClick={() => setSelected(p)}
                  />
                </Marker>
              ))}
            </ZoomableGroup>
          </ComposableMap>
        )}

        {/* Hover tooltip */}
        {hover && !selected && (
          <div
            className="absolute pointer-events-none bg-[#0F0F10] text-white text-[11px] px-2.5 py-1.5 rounded-md shadow-xl z-10"
            style={{ top: 12, right: 12 }}
          >
            <p className="font-bold">{hover.city}</p>
            <p className="opacity-80">{hover.country} · {hover.visitors} visitor{hover.visitors === 1 ? '' : 's'}</p>
          </div>
        )}

        {/* Click-to-drill city detail */}
        {selected && (
          <div
            className="absolute top-3 right-3 bg-white rounded-xl shadow-2xl border border-[var(--hairline)] p-4 w-64 z-20"
            data-testid="geo-selected-city"
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <p className="font-display text-lg font-[700] truncate">{selected.city}</p>
              <button onClick={() => setSelected(null)} className="w-6 h-6 rounded-full hover:bg-black/[0.05] flex items-center justify-center">
                <X size={13} />
              </button>
            </div>
            <p className="text-[11px] text-[#8A8A93] mb-3">{selected.country}</p>
            <div className="bg-[var(--surface-2)] rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Users size={11} className="text-[#B91C1C]" />
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93]">Visitors</p>
              </div>
              <p className="font-display text-2xl font-[700] text-[#B91C1C] tabular-nums">{selected.visitors.toLocaleString()}</p>
              <p className="text-[11px] text-[#8A8A93] mt-1">{((selected.visitors / totalVisitors) * 100).toFixed(1)}% of total traffic</p>
            </div>
            <p className="text-[10px] text-[#8A8A93] mt-2 leading-relaxed">
              {selected.lat?.toFixed(3)}, {selected.lng?.toFixed(3)}
            </p>
          </div>
        )}
      </div>

      <div className="px-4 py-2 flex items-center justify-between text-[10px] text-[#8A8A93] bg-white/40 border-t border-[var(--hairline)]">
        <span>Scroll to zoom · drag to pan · click any dot for detail</span>
        <span className="flex items-center gap-3">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#B91C1C]" /> 1-10</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#B91C1C]" /> 10-100</span>
          <span className="flex items-center gap-1"><span className="w-4 h-4 rounded-full bg-[#B91C1C]" /> 100+</span>
        </span>
      </div>
    </div>
  );
}
